/**
 * Abschnitt „Stoffe und Abfälle" (ELiA-Formular 3.5 und Folgeformulare)
 *
 * Anders als die übrigen Abschnitte ist dies eine **Liste**: Ein Stoff hat 38 Attribute, ein
 * Anlagentyp führt ein Dutzend Stoffe. Die Tabelle zeigt deshalb nur die Spalten, die auch ELiA
 * in seiner Übersichtstabelle führt – Bezeichnung, Gesamtmenge, Einheit, Art des Produktes,
 * AVV-Nummer –, alles Weitere steht im Zeilendialog.
 *
 * Die Komponente kennt weder Anlagentyp noch Anlage: Sie bekommt die Zeilen und vier Rückrufe.
 * So bedient dieselbe Liste die zentrale Vorlage und später die Kopie im Projekt.
 */
import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SubSection from "../../../layout/SubSection";
import BaseTable, { TableColumn } from "../../../tables/common/BaseTable";
import Button from "../../../common/inputs/Button";
import StoffDialog from "./StoffDialog";
import {
  StoffCreate,
  StoffFelder,
  StoffUpdate,
} from "../../../../services/api/anlagentypen";
import { WertelistenOptions, Option } from "../hooks/useWertelisten";
import { colors, fonts } from "../../../../styles/theme";

/**
 * Die Liste kennt ihren Besitzer nicht: Am Anlagentyp und an der Anlage im Projekt tragen die
 * Zeilen dieselben Felder, nur der Fremdschlüssel unterscheidet sich – und den braucht die
 * Darstellung nicht.
 */
export type StoffZeile = StoffFelder & { id: string };

const SPALTEN: TableColumn[] = [
  { key: "bezeichnung", label: "Bezeichnung", width: "28%" },
  { key: "komponentenname", label: "Komponente", width: "24%" },
  { key: "gesamtmenge", label: "Menge", align: "right", width: "12%" },
  { key: "einheit", label: "Einheit", width: "12%" },
  { key: "eigenschaften", label: "Eigenschaften", width: "24%" },
];

/** Kurzzeichen der vier Kennzeichen – sie sagen, welche Folgeformulare der Stoff erzeugt */
const eigenschaften = (stoff: StoffZeile): string =>
  [
    stoff.ist_gefahrstoff && "7.2",
    stoff.ist_wassergefaehrdend && "11.1",
    stoff.ist_entstehender_abfall && "9.1",
    stoff.ist_reach_relevant && "15.1",
  ]
    .filter(Boolean)
    .join(" · ") || "–";

interface StoffListeSectionProps {
  stoffe: StoffZeile[];
  wertelisten: WertelistenOptions;
  loading?: boolean;
  /** Fehlt beim Anlegen: Kindzeilen brauchen einen Elternteil, den es dann noch nicht gibt */
  hinweisStattListe?: string;
  onCreate: (werte: StoffCreate) => Promise<void>;
  onUpdate: (stoffId: string, werte: StoffUpdate) => Promise<void>;
  onDelete: (stoffId: string) => Promise<void>;
  defaultExpanded?: boolean;
}

const StoffListeSection: React.FC<StoffListeSectionProps> = ({
  stoffe,
  wertelisten,
  loading = false,
  hinweisStattListe,
  onCreate,
  onUpdate,
  onDelete,
  defaultExpanded = false,
}) => {
  const [dialogOffen, setDialogOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<StoffZeile | null>(null);

  const bezeichnungVon = (optionen: Option[], id?: string | null) =>
    optionen.find((o) => o.value === id)?.label ?? "";

  const zeilen = stoffe.map((s) => ({
    ...s,
    einheit: bezeichnungVon(wertelisten.einheit, s.einheit_id),
    eigenschaften: eigenschaften(s),
    komponentenname: s.komponentenname ?? "",
  }));

  const oeffnen = (stoff: StoffZeile | null) => {
    setBearbeitet(stoff);
    setDialogOffen(true);
  };

  return (
    <SubSection
      title="Stoffe und Abfälle"
      defaultExpanded={defaultExpanded}
      actions={
        !hinweisStattListe && (
          <Button startIcon={<AddIcon />} onClick={() => oeffnen(null)}>
            Stoff anlegen
          </Button>
        )
      }
    >
      {hinweisStattListe ? (
        <Typography
          sx={{
            fontFamily: fonts.primary,
            fontSize: "0.875rem",
            color: colors.text.secondary,
          }}
        >
          {hinweisStattListe}
        </Typography>
      ) : (
        <Box>
          <BaseTable
            columns={SPALTEN}
            data={zeilen}
            loading={loading}
            fixedLayout
            emptyMessage="Noch keine Stoffe erfasst."
            onRowClick={(row) =>
              oeffnen(stoffe.find((s) => s.id === row.id) ?? null)
            }
            onDelete={(row) => void onDelete(row.id)}
          />
          <StoffDialog
            open={dialogOffen}
            stoff={bearbeitet}
            wertelisten={wertelisten}
            onClose={() => setDialogOffen(false)}
            onSubmit={async (werte) => {
              if (bearbeitet) {
                await onUpdate(bearbeitet.id, werte);
              } else {
                await onCreate(werte);
              }
            }}
          />
        </Box>
      )}
    </SubSection>
  );
};

export default StoffListeSection;
